import { Document } from '@contentful/rich-text-types';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconEndAdornment } from 'components/Button';
import { OrderedList } from 'components/Lists';
import { Heading } from 'components/Typography';
import { StructuredActivityCtx, withStructuredActivityContext } from 'domains/structuredActivities/context';
import { structuredActivityStepId } from 'domains/structuredActivities/steps';
import { Card } from 'components/Card';
import { IconButton } from 'components/IconButton';
import { MarkdownText } from 'domains/contentful/MarkdownText';
import { Accordion, AccordionItem, RichText } from '@expressable/syntax-ds';

const StrategyOverviewContent: React.FC = () => {
  const {
    structuredActivityContent: content,
    onNavigateToStep,
    associatedMaterial,
    previewMode,
  } = useContext(StructuredActivityCtx);
  const { t } = useTranslation();

  const handleContinue = () => onNavigateToStep(structuredActivityStepId.submitActivity, true);

  const { homeworkGoals, whatYoullNeed, aboutThisActivity, videoFile } = content;

  const {
    step1Description,
    step1Title,
    step2Description,
    step2Title,
    step3Description,
    step3Title,
    step4Title,
    step4Description,
    step5Title,
    step5Description,
    whatToDoIfYourChildGetsStuck,
    whatToLookForInChildsActions,
  } = content;

  return (
    <>
      <section>
        <Heading level="h4" useDefaultMargins>
          {t('structuredActivity.practiceOverview.title')}
        </Heading>
        <MarkdownText>{aboutThisActivity}</MarkdownText>
      </section>

      {associatedMaterial && (
        <section className="flex gap-16 items-center">
          <IconButton
            name="Download"
            title="download asset"
            variant="secondary"
            size="lg"
            as="a"
            href={associatedMaterial.materialContentfulUrl}
            target="_blank"
            className="shrink-0"
          />

          <Button variant="link" as="a" href={associatedMaterial.materialContentfulUrl} target="_blank">
            {t('structuredActivity.practiceOverview.downloadAsset', {
              title: associatedMaterial.materialContentfulTitle,
            })}
          </Button>
        </section>
      )}

      {videoFile && (
        <section className="flex flex-col gap-16">
          {videoFile.map(({ sys: { id }, fields: { file } }) => (
            <video key={id} style={{ height: '100%', width: '100%' }} controls>
              <source src={file?.url} />
            </video>
          ))}
        </section>
      )}

      <section>
        <Heading level="h4" useDefaultMargins>
          {t('structuredActivity.practiceOverview.goals')}
        </Heading>
        {homeworkGoals && <RichText>{homeworkGoals as Document}</RichText>}
      </section>

      <section>
        <Heading level="h4" useDefaultMargins>
          {t('structuredActivity.practiceOverview.whatYoullNeed')}
        </Heading>
        {whatYoullNeed && <RichText>{whatYoullNeed as Document}</RichText>}
      </section>

      <section>
        <Heading level="h4" useDefaultMargins>
          {t('structuredActivity.practiceOverview.guidelines')}
        </Heading>

        <OrderedList>
          {step1Title && step1Description && (
            <OrderedList.Item number={1}>
              <Heading level="h5" className="mb-8 font-semibold">
                {step1Title}
              </Heading>
              <MarkdownText>{step1Description}</MarkdownText>
            </OrderedList.Item>
          )}
          {step2Title && step2Description && (
            <OrderedList.Item number={2}>
              <Heading level="h5" className="mb-8 font-semibold">
                {step2Title}
              </Heading>
              <MarkdownText>{step2Description}</MarkdownText>
            </OrderedList.Item>
          )}
          {step3Title && step3Description && (
            <OrderedList.Item number={3}>
              <Heading level="h5" className="mb-8 font-semibold">
                {step3Title}
              </Heading>
              <MarkdownText>{step3Description}</MarkdownText>
            </OrderedList.Item>
          )}
          {step4Title && step4Description && (
            <OrderedList.Item number={4}>
              <Heading level="h5" className="mb-8 font-semibold">
                {step4Title}
              </Heading>
              <MarkdownText>{step4Description}</MarkdownText>
            </OrderedList.Item>
          )}
          {step5Title && step5Description && (
            <OrderedList.Item number={5}>
              <Heading level="h5" className="mb-8 font-semibold">
                {step5Title}
              </Heading>
              <MarkdownText>{step5Description}</MarkdownText>
            </OrderedList.Item>
          )}
        </OrderedList>
      </section>

      <Button
        className="sm-d:w-full"
        size="lg"
        onClick={handleContinue}
        endAdornment={<IconEndAdornment name="AngleRight" />}
        disabled={previewMode}
      >
        {t('structuredActivity.practiceOverview.submit')}
      </Button>

      {(whatToDoIfYourChildGetsStuck || whatToLookForInChildsActions) && (
        <Accordion title={t('structuredActivity.practiceOverview.supportTitle')}>
          {whatToLookForInChildsActions && (
            <AccordionItem title={t('structuredActivity.practiceOverview.whatToLookFor')}>
              <RichText>{whatToLookForInChildsActions as Document}</RichText>
            </AccordionItem>
          )}

          {whatToDoIfYourChildGetsStuck && (
            <AccordionItem title={t('structuredActivity.practiceOverview.whatIfItFeelstough')}>
              <RichText>{whatToDoIfYourChildGetsStuck as Document}</RichText>
            </AccordionItem>
          )}
        </Accordion>
      )}

      <Card className="bg-yellow-300 w-full">
        <Heading level="h4" className="mb-40 sm-d:mb-32">
          {t('dailyRoutine.practiceOverview.feedbackCallout.text')}
        </Heading>

        <Button
          className="sm-d:w-full"
          size="lg"
          onClick={handleContinue}
          endAdornment={<IconEndAdornment name="AngleRight" />}
          disabled={previewMode}
        >
          {t('dailyRoutine.practiceOverview.feedbackCallout.button')}
        </Button>
      </Card>
    </>
  );
};

export default withStructuredActivityContext(StrategyOverviewContent, structuredActivityStepId.overview);
